import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PortfolioAs } from '../PortfolioSubnav.component';
import LinkToIcon from 'icons/LinkTo.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { getDataRoomRoute } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

type UsePortfolioLinksType = {
  as: PortfolioAs;
  dataRoomId: string;
  hasListing: boolean;
  sessionId?: string;
  unreadMessagesCount?: number;
  canAccessDataRoom: boolean;
  canSeeAllTabs?: boolean;
};

export default function usePortfolioLinks(config: UsePortfolioLinksType) {
  const {
    as,
    dataRoomId,
    hasListing,
    sessionId,
    unreadMessagesCount,
    canAccessDataRoom,
    canSeeAllTabs,
  } = config;

  const { isRWCMember } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const sellerLinks = useMemo(
    () => [
      {
        to: 'deal',
        label: t(isPhablet ? 'overview' : 'deal'),
      },
      ...(canSeeAllTabs
        ? [
            {
              to: 'leads',
              label: t('leads'),
            },
          ]
        : []),
      ...(!isRWCMember
        ? [
            {
              to: 'listing-information',
              label: t('listing'),
            },
          ]
        : []),
      ...(canSeeAllTabs
        ? [
            {
              to: 'settings',
              label: t('automation'),
            },
          ]
        : []),
      ...(canAccessDataRoom
        ? [
            {
              to: getDataRoomRoute(dataRoomId),
              label: t('dataRoomUC'),
              Icon: LinkToIcon,
              openInNewTab: true,
            },
          ]
        : []),
    ],
    [t, isPhablet, canSeeAllTabs, isRWCMember, canAccessDataRoom, dataRoomId],
  );

  const buyerLinks = useMemo(
    () => [
      ...(hasListing
        ? [
            {
              to: 'listing-information',
              label: t('deal'),
            },
          ]
        : []),
      ...(sessionId
        ? [
            {
              to: `messaging/${sessionId}`,
              label: t('chat'),
              ...(!!unreadMessagesCount && { count: unreadMessagesCount }),
            },
          ]
        : []),
      {
        to: 'profile',
        label: t('buyerProfile'),
      },
      ...(canAccessDataRoom
        ? [
            {
              to: getDataRoomRoute(dataRoomId, 'documents'),
              label: t('dataRoomUC'),
              Icon: LinkToIcon,
              openInNewTab: true,
            },
          ]
        : []),
    ],
    [
      dataRoomId,
      canAccessDataRoom,
      hasListing,
      sessionId,
      unreadMessagesCount,
      t,
    ],
  );

  return { seller: sellerLinks, buyer: buyerLinks }[as];
}
